import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "reset-password-form" }
const _hoisted_2 = {
  key: 0,
  class: "mfa-sms"
}
const _hoisted_3 = { class: "tel-input" }
const _hoisted_4 = { class: "mt-2 login-checkbox" }
const _hoisted_5 = { class: "ly-row mt-2 ly-even-2" }
const _hoisted_6 = {
  key: 1,
  class: "mfa-authapp"
}
const _hoisted_7 = { class: "authenticator-placeholder text-center mt-2" }
const _hoisted_8 = { class: "text-center" }
const _hoisted_9 = {
  class: "container items-center",
  style: {"justify-content":"center","display":"flex","align-items":"center","flex-direction":"column"}
}
const _hoisted_10 = { class: "mb-2" }
const _hoisted_11 = { class: "text-center mb-half" }
const _hoisted_12 = {
  key: 0,
  class: "mt-0 ly-row mfa-secret-container"
}
const _hoisted_13 = { class: "w-95 border border-black-20 p-half ly-row ly-center-center rounded" }
const _hoisted_14 = { class: "mt-1 w-35 ly-container large-number-container mfa-input-container" }
const _hoisted_15 = { class: "ly-row mt-2 ly-even-2" }
const _hoisted_16 = {
  key: 2,
  class: "msg msg-error w-100 text-center"
}

import { inject } from 'vue'
import { Redirect } from '@/types/redirect'
import { ref, onMounted, watch, nextTick } from 'vue'
import { MFAMethod, MFACountry, getState, resetRedirectAfterMFA, initSmsMfa } from '@/services/mfa'
import { toCanvas } from 'qrcode'
import type { EnrollmentInput, EnrollmentResponse } from '@/services/mfa'
import type { AuthenticationResult } from '@/types/authenticationResult'


export default /*@__PURE__*/_defineComponent({
  __name: 'MfaForm',
  setup(__props) {

const method_options = [
  { name: 'SMS', value: MFAMethod.SMS },
  { name: 'Authenticator App', value: MFAMethod.Authenticator },
  /* { name: 'Email', value: MFAMethod.Email } */
]

const country_options = [
  { name: 'United States of America', value: MFACountry.US },
  { name: 'Canada', value: MFACountry.CA }
]

const country_codes = {
  [MFACountry.US]: '+1',
  [MFACountry.CA]: '+1'
}

const redirect = inject('redirect') as Redirect
const requestQrCodeSecret = inject('enrollAuthenticator') as ({ access_token }: { access_token: string }) => Promise<string | null>
const enrollSMS = inject('enrollSMS') as ({ username, phone_number, access_token }: EnrollmentInput) => Promise<{ session: string | null, sentTo: string | null }>
const verifyEnrollmentAuthenticator = inject('verifyEnrollmentAuthenticator') as ({ access_token, code }: { access_token: string, code: string }) => Promise<EnrollmentResponse>
const redirectToRetrieveToken = inject('redirectToRetrieveToken') as (authenticationResult: AuthenticationResult) => Promise<void>

const method = ref(MFAMethod.Authenticator)
const country = ref(MFACountry.US)
const errorMessage = ref('')
const acceptCharges = ref(false)
const qrSecret = ref<string | null>(null)
const QRCodeCanvas = ref(null)
const mfaSecret = ref<string | null>('')
const showSecret = ref(false)
const code = ref('')
const phone = ref('')
const { authenticationResult, username } = getState()
const isSubmitting = ref(false)

onMounted(async () => {
  qrSecret.value = await requestQrCodeSecret({ access_token: authenticationResult?.AccessToken || '' })
  genQRCode(qrSecret.value || '', username || '')
  mfaSecret.value = qrSecret.value
})

watch(method, (newMethod) => {
  if (newMethod === MFAMethod.Authenticator && qrSecret.value) {
    nextTick(() => QRCodeCanvas.value ? genQRCode(qrSecret.value!, username || '') : null)
  }
})

const genQRCode = (secret: string, username: string) => {
  if (!secret || !username || !QRCodeCanvas.value) return
  const path = `otpauth://totp/${username}?secret=${secret}&issuer=${window.location.host}`;
  toCanvas(QRCodeCanvas.value, path, (error: unknown) => {
    if (error) {
      console.error(error)
    }
  });
};

const goBack = () => redirect('LoginView')

const copyToClipboard = async (event: Event) => {
  event.preventDefault()

  try {
    await navigator.clipboard.writeText(mfaSecret.value ?? '')
  } catch(error) {
    console.error(error)
  }
}

const verify = async (event: Event) => {
  event.preventDefault()
  isSubmitting.value = true
  errorMessage.value = ''

  if (method.value === MFAMethod.SMS) {
    const countryCode = country_codes[country.value]
    const phone_number = `${countryCode}${phone.value}`

    const payload = {
      phone_number,
      username: username || '',
      access_token: authenticationResult?.AccessToken || ''
    }

    const { session, sentTo } = await enrollSMS(payload)
    if (!session || !sentTo) {
      isSubmitting.value = false
      errorMessage.value = 'Error starting the enrollment process, please try again later'
    }

    if (session && sentTo) initSmsMfa(session, sentTo)

    redirect('MFASMSConfirm')
  } else {
    const { success } = await verifyEnrollmentAuthenticator({
      access_token: authenticationResult?.AccessToken || '',
      code: code.value
    })

    if (!success) {
      isSubmitting.value = false
      errorMessage.value = 'Invalid MFA Code. The code you entered is incorrect or has expired.'
    }

    resetRedirectAfterMFA()
    if (success && authenticationResult) redirectToRetrieveToken(authenticationResult)
  }

  isSubmitting.value = false
}


return (_ctx: any,_cache: any) => {
  const _component_lp_select = _resolveComponent("lp-select")!
  const _component_lp_input = _resolveComponent("lp-input")!
  const _component_lp_checkbox = _resolveComponent("lp-checkbox")!
  const _component_lp_button = _resolveComponent("lp-button")!
  const _component_lp_spinner = _resolveComponent("lp-spinner")!
  const _component_lp_tooltip = _resolveComponent("lp-tooltip")!

  return (_openBlock(), _createElementBlock("form", _hoisted_1, [
    _cache[12] || (_cache[12] = _createElementVNode("h1", { class: "text-center" }, "Multi-factor Authentication", -1)),
    _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-center mb-2" }, [
      _createTextVNode("Based on your email domain, your organization requires Multi-Factor Authentication (MFA) for continued access. "),
      _createElementVNode("br"),
      _createElementVNode("br"),
      _createTextVNode("To proceed, please choose one of the available MFA methods below and complete the setup.")
    ], -1)),
    _createVNode(_component_lp_select, {
      "label-top": "Method",
      options: method_options,
      modelValue: method.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((method).value = $event))
    }, null, 8, ["modelValue"]),
    (method.value === _unref(MFAMethod).SMS)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_lp_select, {
            "label-top": "Country",
            options: country_options,
            modelValue: country.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((country).value = $event))
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_lp_input, {
              compact: "",
              "label-top": "Phone",
              placeholder: "(888) 888-8888",
              required: "",
              type: "tel",
              "max-length": 11,
              prefix: country_codes[country.value as keyof typeof country_codes],
              modelValue: phone.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((phone).value = $event))
            }, null, 8, ["prefix", "modelValue"]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_lp_checkbox, {
                type: "primary",
                modelValue: acceptCharges.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((acceptCharges).value = $event)),
                label: "I consent to receive SMS messages for Multi-factor authentication. Standard messaging rates may apply. "
              }, null, 8, ["modelValue"])
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", {
              onClick: goBack,
              type: "button"
            }, " Go Back"),
            _createVNode(_component_lp_button, {
              onClick: verify,
              type: "primary",
              loading: isSubmitting.value,
              disabled: isSubmitting.value || !phone.value || phone.value?.length < 10 || !acceptCharges.value
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("Verify", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"])
          ])
        ]))
      : _createCommentVNode("", true),
    (method.value === _unref(MFAMethod).Authenticator)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-center" }, "To enroll your account, scan the QR code below using an authenticator app:", -1)),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                (!qrSecret.value)
                  ? (_openBlock(), _createBlock(_component_lp_spinner, {
                      key: 0,
                      "data-ua-unique-id": "ua-login-totp-form-spinner",
                      style: {"margin-bottom":"-130px"}
                    }))
                  : _createCommentVNode("", true),
                _createElementVNode("canvas", {
                  ref_key: "QRCodeCanvas",
                  ref: QRCodeCanvas
                }, null, 512)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("p", _hoisted_11, [
              _cache[7] || (_cache[7] = _createTextVNode("Can't read the QR code? ", -1)),
              _createElementVNode("span", {
                onClick: _cache[4] || (_cache[4] = ($event: any) => (showSecret.value = !showSecret.value)),
                class: "mini-button"
              }, "Use secret key")
            ]),
            (showSecret.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("span", null, _toDisplayString(mfaSecret.value), 1)
                  ]),
                  _createVNode(_component_lp_tooltip, {
                    content: "Use this key in your authenticator app to generate your code",
                    placement: "top"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_lp_button, {
                        type: "primary",
                        icon: "copy",
                        onClick: copyToClipboard
                      })
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-center" }, "Enter the code to verify enrollment", -1)),
          _createElementVNode("div", _hoisted_14, [
            _createVNode(_component_lp_input, {
              modelValue: code.value,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((code).value = $event)),
              "max-length": 6,
              class: "text-center font-weight-bold",
              placeholder: "______"
            }, null, 8, ["modelValue"])
          ]),
          _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-center" }, [
            _createTextVNode("Don't have an authenticator app? "),
            _createElementVNode("br"),
            _createTextVNode(" Download an authenticator app like Google"),
            _createElementVNode("br"),
            _createTextVNode(" Authenticator from "),
            _createElementVNode("a", {
              href: "https://apps.apple.com/us/app/google-authenticator/id388497605",
              target: "_blank"
            }, "App Store"),
            _createTextVNode(" or "),
            _createElementVNode("a", { href: "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pli=1" }, "Google Play")
          ], -1)),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("button", {
              onClick: goBack,
              type: "button"
            }, " Go Back"),
            _createVNode(_component_lp_button, {
              onClick: verify,
              type: "primary",
              loading: isSubmitting.value,
              disabled: isSubmitting.value || !code.value || code.value?.length !== 6
            }, {
              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                _createTextVNode("Verify", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"])
          ])
        ]))
      : _createCommentVNode("", true),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})